import { useState } from "react";
import { Sparkles, Clock, Users, CheckCircle, Download, Edit3, Save, Target, Zap, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";

const defaultContent = {
  brandName: "Thrive with Deepa",
  hookLine: "Feeling Stuck in Your Career?",
  section1Badge: "Free · Limited Spots",
  section1Title: "Unlock Your Next",
  section1TitleHighlight: "Big Move",
  section1Subtitle: "A 45-Minute Private Coaching Clarity Call — Complimentary",
  painPoints: [
    "Feeling stuck despite years of experience?",
    "Not sure what your next career move should be?",
    "Struggling with imposter syndrome or self-doubt?",
    "Wondering how to lead with authentic confidence?",
  ],
  section1Benefits: [
    "Get crystal clarity on what's holding you back",
    "Uncover blind spots limiting your leadership",
    "Walk away with 3 actionable next steps",
    "Experience coaching in a safe, private space",
  ],
  urgency: "⚡ Only 5 spots available each week",
  section1CTA: "🎯 Reserve Your Spot Today",
  section1Link: "deepa-coaching.com/#clarity-call",
  credentials: "Deepa Prashanth — Leadership & Career Coach | 275+ Coaching Hours | ICF Level 1 Trained | PROSCI Certified",
  contact: "bookings@deepa-coaching.com",
  testimonial: "Deepa's coaching approach is incredibly insightful and transformative. She knows how to make you feel empowered and ready to tackle any challenge.",
};

const ClarityPoster = () => {
  const [content, setContent] = useState(defaultContent);
  const [isEditing, setIsEditing] = useState(false);

  const handleDownload = () => window.print();
  const toggleEdit = () => setIsEditing(!isEditing);

  const updateField = (field, value) => {
    setContent((prev) => ({ ...prev, [field]: value }));
  };

  const updateArrayItem = (arrayKey, index, value) => {
    setContent((prev) => ({
      ...prev,
      [arrayKey]: prev[arrayKey].map((b, i) => (i === index ? value : b)),
    }));
  };

  const EditableText = ({ field, className, tag = "span", style = {} }) => {
    const Tag = tag;
    if (isEditing) {
      return (
        <Tag
          contentEditable
          suppressContentEditableWarning
          onBlur={(e) => updateField(field, e.target.textContent)}
          className={`${className} outline-2 outline-dashed outline-primary/50 outline-offset-2 focus:outline-primary`}
          style={style}
        >
          {content[field]}
        </Tag>
      );
    }
    return <Tag className={className} style={style}>{content[field]}</Tag>;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-pink-50 to-primary/10 py-8 px-4 print:p-0 print:bg-white">
      {/* Action Buttons */}
      <div className="max-w-2xl mx-auto mb-6 flex flex-wrap justify-center gap-3 print:hidden">
        <Button
          onClick={toggleEdit}
          data-testid="edit-poster-btn"
          className={isEditing ? "bg-green-600 hover:bg-green-700 text-white" : "bg-primary text-white hover:bg-primary/90"}
        >
          {isEditing ? <><Save className="mr-2 h-4 w-4" />Save Changes</> : <><Edit3 className="mr-2 h-4 w-4" />Edit Poster</>}
        </Button>
        <Button onClick={handleDownload} className="bg-primary text-white hover:bg-primary/90">
          <Download className="mr-2 h-4 w-4" />Print / Save as PDF
        </Button>
        <Button onClick={() => (window.location.href = "/")} variant="outline">Back to Home</Button>
      </div>

      {isEditing && (
        <div className="max-w-2xl mx-auto mb-4 p-3 bg-yellow-50 border border-yellow-300 rounded-lg text-center text-sm print:hidden">
          ✏️ <strong>Editing Mode:</strong> Click any text to edit it. Click "Save Changes" when done.
        </div>
      )}

      {/* Poster */}
      <div className="max-w-2xl mx-auto bg-white rounded-3xl shadow-2xl overflow-hidden print:shadow-none print:rounded-none">
        {/* Hook Header */}
        <div 
          className="relative h-48 bg-cover bg-center"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1768744781410-757053db2ab0?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200')" }}
        >
          <div className="absolute inset-0 bg-black/40" />
          <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
            <EditableText
              field="hookLine"
              tag="h1"
              className="text-3xl md:text-4xl font-bold text-white mb-2"
              style={{ textShadow: "2px 2px 8px rgba(0,0,0,0.6)" }}
            />
            <EditableText
              field="brandName"
              tag="p"
              className="text-2xl text-white"
              style={{ fontFamily: "'Dancing Script', cursive", textShadow: "1px 1px 4px rgba(0,0,0,0.4)" }}
            />
          </div>
        </div>

        <div className="p-8 md:p-10">
          {/* Badge */}
          <div className="flex justify-center mb-6">
            <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-red-100 border-2 border-red-300 rounded-full animate-pulse">
              <Zap className="h-4 w-4 text-red-600" />
              <EditableText field="section1Badge" className="text-sm font-bold uppercase tracking-wider text-red-600" />
            </div>
          </div>

          {/* Main Title */}
          <div className="text-center mb-6">
            <EditableText field="section1Title" tag="h2" className="text-3xl md:text-5xl font-bold tracking-tight mb-1 block" />
            <EditableText field="section1TitleHighlight" tag="h2" className="text-3xl md:text-5xl font-bold tracking-tight text-primary block" />
            <EditableText field="section1Subtitle" tag="p" className="text-sm md:text-base text-muted-foreground italic mt-3" />
          </div>

          {/* Pain Points */}
          <div className="bg-red-50 border-l-4 border-red-400 rounded-r-2xl p-5 mb-6">
            <h3 className="text-sm font-bold mb-3 text-red-800">Does This Sound Like You?</h3>
            <div className="space-y-2">
              {content.painPoints.map((point, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className="text-red-500 font-bold">•</span>
                  {isEditing ? (
                    <span
                      contentEditable
                      suppressContentEditableWarning
                      onBlur={(e) => updateArrayItem("painPoints", i, e.target.textContent)}
                      className="text-sm outline-2 outline-dashed outline-primary/50 outline-offset-2"
                    >{point}</span>
                  ) : (
                    <span className="text-sm">{point}</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Benefits */}
          <div className="bg-gradient-to-br from-primary/5 to-accent/5 rounded-2xl p-6 mb-6">
            <h3 className="text-base font-bold mb-4 text-center flex items-center justify-center gap-2">
              <Target className="h-4 w-4 text-primary" />
              In Just 45 Minutes, You'll:
            </h3>
            <div className="space-y-2">
              {content.section1Benefits.map((benefit, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  {isEditing ? (
                    <span
                      contentEditable
                      suppressContentEditableWarning
                      onBlur={(e) => updateArrayItem("section1Benefits", i, e.target.textContent)}
                      className="text-sm outline-2 outline-dashed outline-primary/50 outline-offset-2"
                    >{benefit}</span>
                  ) : (
                    <span className="text-sm">{benefit}</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Testimonial */}
          <div className="bg-accent/10 border-l-4 border-accent rounded-r-2xl p-4 mb-6">
            <p className="text-sm italic text-foreground/80">
              "{isEditing ? (
                <span
                  contentEditable
                  suppressContentEditableWarning
                  onBlur={(e) => updateField("testimonial", e.target.textContent)}
                  className="outline-2 outline-dashed outline-primary/50"
                >{content.testimonial}</span>
              ) : content.testimonial}"
            </p>
            <p className="text-xs text-muted-foreground mt-2">— Recent Client</p>
          </div>

          {/* Session Stats */}
          <div className="grid grid-cols-3 gap-3 mb-6">
            <div className="text-center p-3 bg-primary/5 rounded-xl">
              <Clock className="h-5 w-5 text-primary mx-auto mb-1" />
              <p className="text-lg font-bold text-primary">45 min</p>
              <p className="text-xs text-muted-foreground">Session</p>
            </div>
            <div className="text-center p-3 bg-primary/5 rounded-xl">
              <Users className="h-5 w-5 text-primary mx-auto mb-1" />
              <p className="text-lg font-bold text-primary">1:1</p>
              <p className="text-xs text-muted-foreground">Private</p>
            </div>
            <div className="text-center p-3 bg-primary/5 rounded-xl">
              <Sparkles className="h-5 w-5 text-primary mx-auto mb-1" />
              <p className="text-lg font-bold text-primary">$0</p>
              <p className="text-xs text-muted-foreground">100% Free</p>
            </div>
          </div>

          {/* Urgency */}
          <div className="text-center mb-4">
            <EditableText field="urgency" className="text-sm font-semibold text-orange-600" />
          </div>

          {/* CTA */}
          <div className="text-center">
            <div className="inline-block bg-primary text-white px-8 py-4 rounded-full font-bold text-lg mb-3 shadow-lg">
              <EditableText field="section1CTA" />
            </div>
            <p className="text-sm text-muted-foreground mb-1">Apply Now:</p>
            <a 
              href={`https://${content.section1Link}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-base font-bold text-primary hover:underline break-all"
            >{content.section1Link}</a>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-border/50 p-4 bg-primary/5">
          <EditableText field="credentials" tag="p" className="text-xs text-center text-muted-foreground leading-relaxed" />
          <p className="text-center text-sm font-semibold text-primary mt-2">
            📧 <EditableText field="contact" />
          </p>
        </div>

        <div className="bg-primary py-3 text-center">
          <p className="text-2xl font-bold text-white" style={{ fontFamily: "'Dancing Script', cursive" }}>
            {content.brandName}
          </p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm print:hidden">
        <h4 className="font-bold mb-2">📱 How to Share:</h4>
        <ul className="list-disc list-inside space-y-1 text-muted-foreground">
          <li>Click <strong>Edit Poster</strong> to customize any text</li>
          <li>Click <strong>Print / Save as PDF</strong> to download</li>
          <li>Screenshot on phone to share on WhatsApp/Instagram</li>
          <li>All links are clickable when viewed digitally</li>
        </ul>
      </div>

      <style>{`@media print { body { background: white !important; } @page { margin: 0; size: A4; } }`}</style>
    </div>
  );
};

export default ClarityPoster;
